import React, { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Download, Calendar, Package, AlertTriangle, CheckCircle, Clock, X, ChevronDown } from 'lucide-react';
import config from '@/config';
import { useAuth } from '@/contexts/AuthContext';

// Confirmation Dialog Component
function DownloadConfirmDialog({ isOpen, onClose, batch, onConfirm }) {
  if (!isOpen || !batch) return null;

  const isIncomplete = !batch.is_completed;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className={`px-6 py-4 ${isIncomplete ? 'bg-yellow-50 border-b border-yellow-200' : 'bg-green-50 border-b border-green-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isIncomplete ? 'bg-yellow-600 text-white' : 'bg-green-600 text-white'}`}>
                {isIncomplete ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {isIncomplete ? 'Incomplete Batch' : 'Download Batch'}
                </h2>
                <p className="text-sm text-gray-600 mt-0.5">
                  {batch.batch_name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isIncomplete ? (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-yellow-900 mb-2">
                      This batch is not complete
                    </h3>
                    <p className="text-sm text-yellow-800">
                      Not all work records have final images. Downloading now may result in missing files.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Requested:</span>
                  <span className="font-semibold text-gray-900">{batch.number_of_requested_output}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Completed:</span>
                  <span className="font-semibold text-gray-900">{batch.actual_completed_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Missing:</span>
                  <span className="font-semibold text-red-600">
                    {batch.number_of_requested_output - batch.actual_completed_count}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Completion:</span>
                    <span className="font-semibold text-gray-900">{batch.completion_percentage}%</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-700 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <strong>Note:</strong> You can download the available images now, but some files will be missing. 
                Please confirm you understand this before proceeding.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-green-900 mb-2">
                      Batch is complete
                    </h3>
                    <p className="text-sm text-green-800">
                      All work records have been processed and final images are ready.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Images:</span>
                  <span className="font-semibold text-gray-900">{batch.actual_completed_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Completion:</span>
                  <span className="font-semibold text-green-600">100%</span>
                </div>
              </div>

              <p className="text-sm text-gray-700">
                The download will include all {batch.actual_completed_count} final images in a ZIP file.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className={`px-6 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                isIncomplete
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <Download className="w-4 h-4" />
              {isIncomplete ? 'Download Anyway' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkBatchesList() {
  const { authenticatedFetch } = useAuth();
  
  // Data state
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Download state
  const [downloadingBatchId, setDownloadingBatchId] = useState(null);
  const [downloadDialog, setDownloadDialog] = useState({
    isOpen: false,
    batch: null
  });
  
  // Filter state
  const [filters, setFilters] = useState({
    completed_only: null,
    batch_name: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const observerTarget = useRef(null);
  const pageSize = 20;

  // Fetch batches
  const fetchBatches = useCallback(async (page, resetData = false) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      });

      if (filters.completed_only !== null) {
        params.append('completed_only', filters.completed_only.toString());
      }

      if (filters.batch_name) {
        params.append('batch_name', filters.batch_name);
      }

      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/work/work-batches/list-with-stats?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (resetData || page === 1) {
        setBatches(data.batches);
      } else {
        setBatches(prev => [...prev, ...data.batches]);
      }
      
      setTotalCount(data.pagination.total_batches);
      setHasMore(data.pagination.has_next_page);
      setCurrentPage(page);
      setInitialLoading(false);
      
    } catch (err) {
      console.error('Error fetching batches:', err);
      setError(err.message);
      setInitialLoading(false);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, filters]);

  // Download batch
  const downloadBatch = async (batchId) => {
    try {
      setDownloadingBatchId(batchId);
      
      const response = await authenticatedFetch(
        `${config.BACKEND_API_ADDRESS}/work/work-batches/${batchId}/download-final-images`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header if available
      const batch = downloadDialog.batch;
      let filename;

      if (batch?.batch_name) {
        const sanitizedName = batch.batch_name.replace(/[^a-zA-Z0-9-_]/g, '_');
        filename = `${sanitizedName}.zip`;
      } else {
        const contentDisposition = response.headers.get('Content-Disposition');
        filename = 'batch_images.zip';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setDownloadDialog({ isOpen: false, batch: null });
      
    } catch (err) {
      console.error('Error downloading batch:', err);
      alert(`Failed to download batch: ${err.message}`);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  // Open download dialog
  const openDownloadDialog = (batch) => {
    setDownloadDialog({
      isOpen: true,
      batch
    });
  };

  // Close download dialog
  const closeDownloadDialog = () => {
    setDownloadDialog({
      isOpen: false,
      batch: null
    });
  };

  // Confirm download
  const confirmDownload = () => {
    if (downloadDialog.batch) {
      downloadBatch(downloadDialog.batch.id);
    }
  };

  // Toggle batch expansion
  const toggleBatchExpansion = (batchId) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  // Initial load and when filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    fetchBatches(1, true);
  }, [filters, fetchBatches]);

  // Infinite scroll
  useEffect(() => {
    const currentTarget = observerTarget.current;
    
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          fetchBatches(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, currentPage, fetchBatches]);

  const handleRefresh = () => {
    setCurrentPage(1);
    setHasMore(true);
    fetchBatches(1, true);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusBadge = (batch) => {
    if (batch.is_completed) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Complete
        </span>
      );
    } else if (batch.actual_completed_count === 0) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
          <Clock className="w-3 h-3 mr-1" />
          Not Started
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
          <Clock className="w-3 h-3 mr-1" />
          In Progress
        </span>
      );
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Download Confirmation Dialog */}
      <DownloadConfirmDialog
        isOpen={downloadDialog.isOpen}
        onClose={closeDownloadDialog}
        batch={downloadDialog.batch}
        onConfirm={confirmDownload}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Work Batches</h1>
              <p className="text-sm text-gray-600 mt-1">
                {totalCount} total batches
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3">
            <input
              type="text"
              value={filters.batch_name}
              onChange={(e) => handleFilterChange('batch_name', e.target.value)}
              placeholder="Search batch name..."
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <select
              value={filters.completed_only === null ? '' : filters.completed_only.toString()}
              onChange={(e) => handleFilterChange('completed_only', e.target.value === '' ? null : e.target.value === 'true')}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">All Batches</option>
              <option value="true">Completed Only</option>
              <option value="false">Incomplete Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 py-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-semibold text-red-800">Error loading batches</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {batches.length > 0 ? (
              batches.map(batch => {
                const isExpanded = expandedBatches.has(batch.id);
                const isDownloading = downloadingBatchId === batch.id;
                
                return (
                  <div
                    key={batch.id}
                    className={`bg-white rounded-lg shadow-sm border overflow-hidden transition-all ${
                      batch.is_completed
                        ? 'border-green-200 hover:border-green-300'
                        : batch.actual_completed_count === 0
                        ? 'border-red-200 hover:border-red-300'
                        : 'border-yellow-200 hover:border-yellow-300'
                    }`}
                  >
                    {/* Batch Header */}
                    <div
                      className={`px-6 py-4 cursor-pointer transition-colors ${
                        batch.is_completed
                          ? 'bg-green-50 hover:bg-green-100'
                          : batch.actual_completed_count === 0
                          ? 'bg-red-50 hover:bg-red-100'
                          : 'bg-yellow-50 hover:bg-yellow-100'
                      }`}
                      onClick={() => toggleBatchExpansion(batch.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1">
                          <ChevronDown
                            className={`w-5 h-5 text-gray-500 transition-transform ${
                              isExpanded ? 'transform rotate-180' : ''
                            }`}
                          />
                          <Package className="w-5 h-5 text-gray-600" />
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {batch.batch_name}
                            </h3>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-sm text-gray-600">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                {new Date(batch.created_at).toLocaleDateString()}
                              </span>
                              {batch.requested_delivery_date && (
                                <span className="text-sm text-gray-600">
                                  Due: {new Date(batch.requested_delivery_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {getStatusBadge(batch)}
                          
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">
                              {batch.completion_percentage}%
                            </div>
                            <div className="text-xs text-gray-600">
                              {batch.actual_completed_count} / {batch.number_of_requested_output}
                            </div>
                          </div>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (batch.actual_completed_count === 0) {
                                alert('No images available to download yet.');
                              } else {
                                openDownloadDialog(batch);
                              }
                            }}
                            disabled={isDownloading || batch.actual_completed_count === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isDownloading ? (
                              <>
                                <RefreshCw className="w-4 h-4 animate-spin" />
                                <span className="text-sm font-medium">Downloading...</span>
                              </>
                            ) : (
                              <>
                                <Download className="w-4 h-4" />
                                <span className="text-sm font-medium">Download</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mt-3">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all ${
                              batch.is_completed
                                ? 'bg-green-600'
                                : 'bg-yellow-600'
                            }`}
                            style={{ width: `${batch.completion_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-6 py-4 bg-white border-t border-gray-200">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="text-xs text-gray-600 mb-1">Total Requested</div>
                            <div className="text-2xl font-bold text-gray-900">
                              {batch.number_of_requested_output}
                            </div>
                          </div>
                          <div className="bg-green-50 rounded-lg p-4">
                            <div className="text-xs text-green-600 mb-1">Completed</div>
                            <div className="text-2xl font-bold text-green-900">
                              {batch.actual_completed_count}
                            </div>
                          </div>
                          <div className="bg-yellow-50 rounded-lg p-4">
                            <div className="text-xs text-yellow-600 mb-1">In Progress</div>
                            <div className="text-2xl font-bold text-yellow-900">
                              {batch.total_records - batch.actual_completed_count}
                            </div>
                          </div>
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="text-xs text-blue-600 mb-1">Total Records</div>
                            <div className="text-2xl font-bold text-blue-900">
                              {batch.total_records}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 text-sm text-gray-600">
                          <strong>Batch ID:</strong> {batch.id}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : !loading && !initialLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No batches found
                </h3>
                <p className="text-gray-600">
                  {filters.batch_name || filters.completed_only !== null
                    ? 'Try adjusting your filters'
                    : 'Start by creating some work batches'}
                </p>
              </div>
            ) : initialLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
                <span className="text-gray-600 font-medium">Loading batches...</span>
              </div>
            ) : null}
          </div>

          {batches.length > 0 && (
            <div ref={observerTarget} className="py-8 text-center">
              {loading && !initialLoading && (
                <div className="flex items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                  <span className="text-sm text-gray-600">Loading more...</span>
                </div>
              )}
              {!hasMore && (
                <span className="text-sm text-gray-500">No more batches to load</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default WorkBatchesList;